import type { JSX, ReactNode } from "react";
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useParams,
  type RouteObject,
} from "react-router";
import {
  isFeatureEnabled,
  type FeatureName,
} from "@shared-core/config/features";
import { routes, paths } from "@shared-core/routing/routes";
import { FaroRouteTracker } from "../observability/faro-signals";
import { AppLayout } from "./components/AppLayout";
import { FindResultsPage } from "../pages/objects/FindResultsPage";
import { ObjectListPage } from "../pages/objects/ObjectListPage";
import { ObjectRecordPage } from "../pages/objects/ObjectRecordPage";
import { HomeRedirect } from "./components/HomeRedirect";
import { SalesHome } from "../pages/SalesHome";
import { OpsHome } from "../pages/OpsHome";
import { CsrHome } from "../pages/CsrHome";
import { CsrHomeAlt } from "../pages/CsrHomeAlt";
import { FinanceHome } from "../pages/FinanceHome";
import { TrackingPage } from "../pages/TrackingPage";
import { StockItemPage } from "../pages/stock/StockItemPage";
import { StockItemsPage } from "../pages/stock/StockItemsPage";
import { ReplenishmentProposalPage } from "../pages/stock/ReplenishmentProposalPage";
import { ReplenishmentProposalsPage } from "../pages/stock/ReplenishmentProposalsPage";
import { CustomersPage } from "../pages/CustomersPage";
import { CustomerCreatePage } from "../pages/CustomerCreatePage";
import { JourneyTestingConsolePage } from "../pages/journey-testing/JourneyTestingConsolePage";
import { TestingPreviewOpenPage } from "../pages/TestingPreviewOpenPage";
import { CustomerDetailPage } from "../pages/CustomerDetailPage";
import { CreditControlPage } from "../pages/CreditControlPage";
import { CustomerOrderHistoryPage } from "../pages/CustomerOrderHistoryPage";
import { CustomerOrdersPage } from "../pages/CustomerOrdersPage";
import { CustomerQuotesPage } from "../pages/CustomerQuotesPage";
import { CustomerSettingsPage } from "../pages/CustomerSettingsPage";
import { CustomerPlannedPage } from "../pages/CustomerPlannedPage";
import { AlertsPage } from "../pages/AlertsPage";
import { CommercialExceptionsPage } from "../pages/CommercialExceptionsPage";
import { AccountAllocationPage } from "../pages/AccountAllocationPage";
import { CarriageCostsPage } from "../pages/CarriageCostsPage";
import { SettingsPricingTargetsPage } from "../pages/SettingsPricingTargetsPage";
import { SettingsPricingReviewPage } from "../pages/SettingsPricingReviewPage";
import { RecommendationsPage } from "../pages/RecommendationsPage";
import { NotesPage } from "../pages/NotesPage";
import { CommunicationsPage } from "../pages/CommunicationsPage";
import { ProspectsPage } from "../pages/ProspectsPage";
import { LeadsPage } from "../pages/LeadsPage";
import { LeadDetailPage } from "../pages/LeadDetailPage";
import { TicketsPage } from "../pages/TicketsPage";
import { TicketDetailPage } from "../pages/TicketDetailPage";
import { EstimatesPage } from "../pages/EstimatesPage";
import { EstimateDetailPage } from "../pages/EstimateDetailPage";
import { NewMerchandiseRequestPage } from "../pages/merchandise-request/NewMerchandiseRequestPage";
import { CataloguePage } from "../pages/CataloguePage";
import { CatalogueItemPage } from "../pages/CatalogueItemPage";
import { DictionaryAdminPage } from "../pages/DictionaryAdminPage";
import { DictionaryGapLogPage } from "../pages/DictionaryGapLogPage";
import { LiveCostingChecksPage } from "../pages/LiveCostingChecksPage";
import { LiveCostingCostLinePage } from "../pages/LiveCostingCostLinePage";
import { DictionaryItemAdminPage } from "../pages/DictionaryItemAdminPage";
import { DictionaryCostPairPage } from "../pages/DictionaryCostPairPage";
import { DictionaryWorkbookPage } from "../pages/DictionaryWorkbookPage";
import { ContactDetailPage } from "../pages/ContactDetailPage";
import { ContactsPage } from "../pages/ContactsPage";
import { JobsPage } from "../pages/JobsPage";
import { JobWorkspacePage } from "../pages/JobWorkspacePage";
import { OrdersPage } from "../pages/OrdersPage";
import { OrderDetailPage } from "../pages/OrderDetailPage";
import { QuotePage } from "../pages/QuotePage";
import { QuotesPage } from "../pages/QuotesPage";
import { PurchaseOrderDetailPage } from "../pages/PurchaseOrderDetailPage";
import { PurchasingPage } from "../pages/PurchasingPage";
import { PlacementPage } from "../pages/PlacementPage";
import { PromoOfficePage } from "../pages/PromoOfficePage";
import { PromoOfficeQuotePrintPage } from "../pages/PromoOfficeQuotePrintPage";
import { LegacyVarianceAuthorisationRedirect } from "./components/LegacyVarianceAuthorisationRedirect";
import { PurchaseOrderQueuePage } from "../pages/PurchaseOrderQueuePage";
import { SupplierPage } from "../pages/SupplierPage";
import { AgedDebtPage } from "../pages/AgedDebtPage";
import { AwaitingPaymentPage } from "../pages/AwaitingPaymentPage";
import { NeedsAttentionPage } from "../pages/NeedsAttentionPage";
import { SettingsNeedsAttentionRulesPage } from "../pages/SettingsNeedsAttentionRulesPage";
import { CreditNotePage } from "../pages/CreditNotePage";
import { CustomerInvoicesPage } from "../pages/CustomerInvoicesPage";
import { SalesInvoicePage } from "../pages/SalesInvoicePage";
import { ProformaPage } from "../pages/ProformaPage";
import { LedgerRulesPage } from "../pages/LedgerRulesPage";
import { SuppliersPage } from "../pages/SuppliersPage";
import { PrepressHomePage } from "../pages/PrepressHomePage";
import { IssuesPage } from "../pages/IssuesPage";
import { RaiseIssuePage } from "../pages/RaiseIssuePage";
import { IssueDetailPage } from "../pages/IssueDetailPage";
import { InsightsPage } from "../pages/InsightsPage";
import { AccrualsUploadPage } from "../pages/AccrualsUploadPage";
import { ReportsPage } from "../pages/ReportsPage";
import { RepCompanyFiguresPage } from "../pages/RepCompanyFiguresPage";
import { CalendarPage } from "../pages/CalendarPage";
import { CampaignsPage } from "../pages/CampaignsPage";
import { CampaignWorkspacePage } from "../pages/CampaignWorkspacePage";
import { AudiencesPage } from "../pages/AudiencesPage";
import { AudienceBuilderPage } from "../pages/AudienceBuilderPage";
import { MarketingConsentPage } from "../pages/MarketingConsentPage";
import { MarketingResultsPage } from "../pages/MarketingResultsPage";
import { MarketingTriggersPage } from "../pages/MarketingTriggersPage";
import { MarketingTriggerPage } from "../pages/MarketingTriggerPage";
import { LandingPagesPage } from "../pages/LandingPagesPage";
import { LandingPageSubmissionsPage } from "../pages/LandingPageSubmissionsPage";
import { MarketingPeoplePage } from "../pages/MarketingPeoplePage";
import { MarketingPersonDetailPage } from "../pages/MarketingPersonDetailPage";
import { SettingsLayout } from "../pages/SettingsLayout";
import { SettingsLiveCostingLayout } from "../pages/SettingsLiveCostingLayout";
import { ManufacturingSetupPage } from "../pages/ManufacturingSetupPage";
import { ManufacturingSetupCostCentreDetailPage } from "../pages/ManufacturingSetupCostCentreDetailPage";
import { ManufacturingSetupInkProfileDetailPage } from "../pages/ManufacturingSetupInkProfileDetailPage";
import { ManufacturingSetupProductTypeDetailPage } from "../pages/ManufacturingSetupProductTypeDetailPage";
import { ManufacturingSetupMaterialDetailPage } from "../pages/ManufacturingSetupMaterialDetailPage";
import { LiveCostingRoutingPolicyPage } from "../pages/LiveCostingRoutingPolicyPage";
import { LiveCostingStatusPage } from "../pages/LiveCostingStatusPage";
import { LiveCostingCheckSettingsPage } from "../pages/LiveCostingCheckSettingsPage";
import { SettingsSignalsPage } from "../pages/SettingsSignalsPage";
import { SignalEditorPage } from "../pages/SignalEditorPage";
import { SettingsAlertsPage } from "../pages/SettingsAlertsPage";
import { AlertEditorPage } from "../pages/AlertEditorPage";
import { SettingsRecommendationsPage } from "../pages/SettingsRecommendationsPage";
import { RecommendationEditorPage } from "../pages/RecommendationEditorPage";
import { PlanCycleReviewPage } from "../pages/PlanCycleReviewPage";
import { PlanPage } from "../pages/PlanPage";
import { MyPipelinePage } from "../pages/MyPipelinePage";
import { ArrivalsQueuePage } from "../pages/ArrivalsQueuePage";
import { InterventionLogPage } from "../pages/InterventionLogPage";
import { MovementPage } from "../pages/MovementPage";
import { TargetPlanningPage } from "../pages/TargetPlanningPage";
import { FeatureSessionsPage } from "../pages/FeatureSessionsPage";
import { FeatureSessionWorkspacePage } from "../pages/FeatureSessionWorkspacePage";
import { SettingsIntegrationsPage } from "../pages/SettingsIntegrationsPage";
import { SettingsScenariosPage } from "../pages/SettingsScenariosPage";
import { SettingsBulkDefinitionsPage } from "../pages/SettingsBulkDefinitionsPage";
import { SettingsPricingPage } from "../pages/SettingsPricingPage";
import { PriceGroupsPage } from "../pages/PriceGroupsPage";
import { CataloguesPage } from "../pages/CataloguesPage";
import { CatalogueEditorPage } from "../pages/CatalogueEditorPage";
import { AssignmentsPage } from "../pages/AssignmentsPage";
import { SettingsJobsPage } from "../pages/SettingsJobsPage";
import { SharedPreviewPage } from "../pages/SharedPreviewPage";
import { WallboardPage } from "../pages/WallboardPage";
import { ExeterBoardPage } from "../pages/SiteProductionBoardPage";
import { ShopfloorKioskPage } from "../pages/ShopfloorKioskPage";
import { AdminUsersPage } from "../pages/AdminUsersPage";
import { PatternDiscoveryPage } from "../pages/PatternDiscoveryPage";
import { SettingsWallboardPage } from "../pages/SettingsWallboardPage";
import { SettingsShopfloorKioskPage } from "../pages/SettingsShopfloorKioskPage";
import { SettingsFeaturesPage } from "../pages/SettingsFeaturesPage";
import { SettingsFeedbackPage } from "../pages/SettingsFeedbackPage";
import { SettingsFeedbackDetailPage } from "../pages/SettingsFeedbackDetailPage";
import { SettingsTicketCategoriesPage } from "../pages/SettingsTicketCategoriesPage";
import { SettingsBuddiesPage } from "../pages/SettingsBuddiesPage";
import { SettingsDepartmentsPage } from "../pages/SettingsDepartmentsPage";
import { SettingsEmailCaptureExclusionsPage } from "../pages/SettingsEmailCaptureExclusionsPage";
import { SettingsCatalogueMigrationPage } from "../pages/SettingsCatalogueMigrationPage";
import { SettingsCatalogueMigrationCustomerPage } from "../pages/SettingsCatalogueMigrationCustomerPage";
import { CustomerPortalAccessPage } from "../pages/CustomerPortalAccessPage";
import { NotFoundPage } from "../pages/NotFoundPage";
import { ProtectedRoute } from "./auth/ProtectedRoute";

const adminOnly = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute requiredRole="admin">{element}</ProtectedRoute>
);

// Stock control. Mirrors backend Permit.STOCK_READ: every operational staff
// role. Changing stock (STOCK_MANAGE) is gated inside the pages.
const stockTier = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute
    requiredRole={[
      "admin",
      "manager",
      "sales_rep",
      "csr",
      "commercial",
      "marketing",
      "stock_control",
    ]}
  >
    {element}
  </ProtectedRoute>
);

// Stock replenishment (BrandHub doc 09 §8). Mirrors backend
// Permit.REPLENISHMENT_VIEW: admin, manager, csr, marketing and stock_control.
const replenishmentTier = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute
    requiredRole={["admin", "manager", "csr", "marketing", "stock_control"]}
  >
    {element}
  </ProtectedRoute>
);

// The commercial job group's surface: the exceptions board and the
// pricing-target policy, plus admin/manager oversight.
const commercialTier = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute requiredRole={["admin", "manager", "commercial"]}>
    {element}
  </ProtectedRoute>
);

// Dev sessions (feature-sessions) — the techie tier. Admin + manager only.
const techieTier = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute requiredRole={["admin", "manager"]}>{element}</ProtectedRoute>
);

// Catalogue curation + pricing policy. Mirrors backend Permit.CATALOGUE_MANAGE
// and Permit.PRICING_MANAGE (admin + manager). The frontend guard used to say
// admin-only, stricter than the backend, so managers with the permit were
// bounced to the dashboard. Same tier as useCanCurateCatalogue().
const catalogueCuration = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute requiredRole={["admin", "manager"]}>{element}</ProtectedRoute>
);

// Feature state view — mirrors backend Permit.FEATURES_READ
// (admin + engineer). The page shows allowlist membership and every
// unreleased feature name, so it is deliberately not a manager surface.
const canReadFeaturesTier = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute requiredRole={["admin", "engineer"]}>
    {element}
  </ProtectedRoute>
);

// Manufacturing setup (docs/features/cost-engine-productionisation/
// 03_manufacturing_surfaces_design.md) — read-only for admin, engineer and
// manager (product brief §3). Wider than catalogueCuration (which excludes
// engineer) because engineers are named readers of this specific surface.
const manufacturingSetupTier = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute requiredRole={["admin", "manager", "engineer"]}>
    {element}
  </ProtectedRoute>
);

// Ticket category taxonomy — mirrors backend Permit.WORKFLOW_MANAGE_CATEGORIES
// (admin + manager).
const canManageTicketCategoriesTier = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute requiredRole={["admin", "manager"]}>{element}</ProtectedRoute>
);

// CSR account allocation (csr-account-allocation) — mirrors backend
// Permit.ACCOUNT_ALLOCATION_MANAGE (admin + manager). Same tier as
// useCanManageAccountAllocation().
const canManageAccountAllocationTier = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute requiredRole={["admin", "manager"]}>{element}</ProtectedRoute>
);

// Buddy lists (csr-ticket-routing) — admin + manager, per the product
// brief's confirmed "manager/admin only" decision; people don't edit their
// own or others' lists.
const canManageBuddyListsTier = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute requiredRole={["admin", "manager"]}>{element}</ProtectedRoute>
);

// Department admin (department-ticket-routing) — mirrors backend
// Permit.USERS_MANAGE_DEPARTMENTS (admin + manager).
const canManageDepartmentsTier = (element: JSX.Element): JSX.Element => (
  <ProtectedRoute requiredRole={["admin", "manager"]}>{element}</ProtectedRoute>
);

// Email-capture denylist admin (email-capture-exceptions) — mirrors backend
// Permit.CUSTOMER_COMMUNICATIONS_MANAGE_EXCLUSIONS (admin + manager), its
// own dedicated permit rather than the general comms read/write ones.
const canManageEmailCaptureExclusionsTier = (
  element: JSX.Element,
): JSX.Element => (
  <ProtectedRoute requiredRole={["admin", "manager"]}>{element}</ProtectedRoute>
);

/**
 * Renders `children` when the feature is on for this user, and the standard
 * not-found page when it is not.
 *
 * Not-found rather than an "unavailable here" message, for the same reason
 * `require_feature` returns 404 rather than 403: absence must be
 * indistinguishable from never having existed. A distinguishable message tells
 * the person in front of it that there is something they are not being shown.
 *
 * This is presentation only. It stops a user walking into a dead page; it
 * protects nothing, because the URL is still typed and the bundle still holds
 * the component. The gate that refuses is `require_feature` on the router
 * mount — see 05_production_surface_spec.md §5.3.
 */
function FeatureGate({
  feature,
  children,
}: {
  feature: FeatureName;
  children: ReactNode;
}): JSX.Element {
  return isFeatureEnabled(feature) ? <>{children}</> : <NotFoundPage />;
}

/** path template -> owning feature, straight off the route manifest. */
const FEATURE_BY_PATH: ReadonlyMap<string, FeatureName> = new Map(
  Object.values(routes).map((route) => [route.path, route.feature]),
);

/**
 * The only path inside the dashboard shell with no manifest entry: the
 * catch-all, which IS the not-found page and so cannot be gated by one.
 *
 * Every other registered route must resolve from the manifest — that is what
 * makes "every route names a feature" true of the router and not merely of the
 * manifest. `scripts/check_features.py` rule 14 enforces the same thing at
 * check time; this set is the runtime half of the pair.
 */
const UNGATED_SHELL_PATHS: ReadonlySet<string> = new Set(["*"]);

/**
 * Wrap every route in the dashboard shell in its feature's gate.
 *
 * Applied mechanically rather than by hand-wrapping each entry, because
 * hand-wrapping reintroduces exactly the failure the production surface is
 * built to prevent: a page added next month is live in production because
 * someone forgot a wrapper. Here a new route is gated by construction, and an
 * entry whose path is in neither the manifest nor `UNGATED_SHELL_PATHS`
 * **throws at module load** — a blank app on first render, not a silently
 * ungated page in production.
 *
 * Layout routes (no path, only children) are not gated themselves; the
 * recursion gates their children, so one disabled child does not take the
 * layout and its enabled siblings down with it.
 */
function gateShellRoutes(children: RouteObject[]): RouteObject[] {
  return children.map((child): RouteObject => {
    if (child.children) {
      return { ...child, children: gateShellRoutes(child.children) };
    }

    // The index route IS `/`, which the manifest calls `root`.
    const feature = child.index
      ? routes.root.feature
      : FEATURE_BY_PATH.get(child.path ?? "");

    if (feature === undefined) {
      if (UNGATED_SHELL_PATHS.has(child.path ?? "")) return child;
      throw new Error(
        `Route "${child.path}" has no entry in the route manifest, so no feature ` +
          `owns it and nothing would gate it. Add it to ` +
          `packages/shared-core/src/routing/routes.ts with a feature and register ` +
          `it as \`path: routes.<id>.path\`.`,
      );
    }

    const { Component, element, ...rest } = child;
    const inner = Component ? <Component /> : element;
    return {
      ...rest,
      element: <FeatureGate feature={feature}>{inner}</FeatureGate>,
    };
  });
}

const PURCHASE_INVOICE = "finance_purchase_ledger.purchase_invoice";

/**
 * The old matching workbench's URLs (PIM-3b): One's purchase invoice list and record are the only matching surface
 * now. Xero bills carry `/purchase-invoices/{id}` (the push's `bill_url`), so every one keeps working: the workbench
 * opens the list on Needs a look (its `?open=` purchase invoice, that record), the All purchase invoices index the
 * whole list, and a purchase invoice's own page its record.
 */
function LegacyPurchaseInvoicesRedirect(): JSX.Element {
  const open = new URLSearchParams(useLocation().search).get("open");
  return (
    <Navigate
      to={
        open
          ? paths.objectRecord({ objectId: PURCHASE_INVOICE, recordKey: open })
          : paths.objectList({
              objectId: PURCHASE_INVOICE,
              search: "queue=needs_a_look",
            })
      }
      replace
    />
  );
}

function LegacyPurchaseInvoiceIndexRedirect(): JSX.Element {
  return (
    <Navigate
      to={paths.objectList({ objectId: PURCHASE_INVOICE, search: "queue=all" })}
      replace
    />
  );
}

function LegacyPurchaseInvoiceRedirect(): JSX.Element {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  return (
    <Navigate
      to={
        invoiceId
          ? paths.objectRecord({
              objectId: PURCHASE_INVOICE,
              recordKey: invoiceId,
            })
          : paths.objectList({ objectId: PURCHASE_INVOICE })
      }
      replace
    />
  );
}

/**
 * Compatibility redirect for `/estimates/tharstern/:id`.
 *
 * While the catalogue work had estimate detail split in two, that was where the
 * Tharstern page lived. The split is gone and `/estimates/:id` is canonical
 * again, but URLs from that period are out in the world — bookmarks, and links
 * CSRs pasted into Teams — so they keep resolving rather than 404ing. A
 * redirect, deliberately, not a second registration of the page: two live URLs
 * for one estimate is how the ambiguity started.
 */
function LegacyTharsternEstimateRedirect(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  return (
    <Navigate
      to={id ? paths.estimateDetail({ id }) : paths.estimates()}
      replace
    />
  );
}

/**
 * Compatibility redirects for every catalogue-curation and manufacturing
 * URL that moved under Settings › Catalogue on 12 Sep (see the route
 * manifest's "Legacy redirect sources only" section and
 * `03_manufacturing_surfaces_design.md`). Each mirrors the dynamic segments
 * of its old path onto the new one; the segment values are taken straight
 * from `useParams` and re-interpolated rather than round-tripped through a
 * typed `paths.*` builder, because several of them (`dictionaryItemAdmin`,
 * `dictionaryCostPair`, the cost-line id) carry pre-encoded values the
 * builders would otherwise double-encode.
 */
function LegacyDictionaryItemAdminRedirect(): JSX.Element {
  const { dictionaryItemId } = useParams<{ dictionaryItemId: string }>();
  return (
    <Navigate
      to={`/settings/catalogue/dictionary/${dictionaryItemId ?? ""}`}
      replace
    />
  );
}

function LegacyDictionaryCostPairRedirect(): JSX.Element {
  const { dictionaryItemId, variantId, quantity } = useParams<{
    dictionaryItemId: string;
    variantId: string;
    quantity: string;
  }>();
  return (
    <Navigate
      to={`/settings/catalogue/dictionary/${dictionaryItemId ?? ""}/pair/${variantId ?? ""}/${quantity ?? ""}`}
      replace
    />
  );
}

// `view`/`costCentreId`/`productTypeId` ride in the query string, not a
// path param, so the redirect must carry `location.search` forward too —
// otherwise a deep link into a specific view or filter would silently land
// on the Checks tab's default view instead.
function LegacyDictionaryRollingValidationRedirect(): JSX.Element {
  const location = useLocation();
  return (
    <Navigate
      to={`${paths.settingsLiveCostingChecks()}${location.search}`}
      replace
    />
  );
}

function LegacyDictionaryCostLineRedirect(): JSX.Element {
  const { costLineId } = useParams<{ costLineId: string }>();
  return (
    <Navigate
      to={`/settings/catalogue/live-costing/checks/cost-lines/${costLineId ?? ""}`}
      replace
    />
  );
}

function LegacyManufacturingCostCentreDetailRedirect(): JSX.Element {
  const { costCentreId } = useParams<{ costCentreId: string }>();
  return (
    <Navigate
      to={`/settings/catalogue/live-costing/manufacturing-setup/cost-centres/${costCentreId ?? ""}`}
      replace
    />
  );
}

function LegacyManufacturingInkProfileDetailRedirect(): JSX.Element {
  const { inkProfileId } = useParams<{ inkProfileId: string }>();
  return (
    <Navigate
      to={`/settings/catalogue/live-costing/manufacturing-setup/ink-profiles/${inkProfileId ?? ""}`}
      replace
    />
  );
}

function LegacyManufacturingProductTypeDetailRedirect(): JSX.Element {
  const { productTypeId } = useParams<{ productTypeId: string }>();
  return (
    <Navigate
      to={`/settings/catalogue/live-costing/manufacturing-setup/product-types/${productTypeId ?? ""}`}
      replace
    />
  );
}

function LegacyManufacturingMaterialDetailRedirect(): JSX.Element {
  const { code } = useParams<{ code: string }>();
  return (
    <Navigate
      to={`/settings/catalogue/live-costing/manufacturing-setup/materials/${code ?? ""}`}
      replace
    />
  );
}

/**
 * The application's route table. Exported so tests can drive the real config
 * through a memory router — a feature gate that is only proved against a
 * hand-built copy of the routes proves nothing about the app.
 */
export const routeConfig: RouteObject[] = [
  {
    path: routes.root.path,
    // Classic's DashboardLayout, or One's object sidebar when One is chosen.
    Component: AppLayout,
    // Every child is wrapped in its feature's gate. The layout itself is not:
    // it is the chrome, and it must still render so a gated-off page shows the
    // not-found body inside a normal-looking app rather than a blank document.
    children: gateShellRoutes([
      { index: true, Component: HomeRedirect },
      { path: routes.objectList.path, Component: ObjectListPage },
      { path: routes.objectRecord.path, Component: ObjectRecordPage },
      { path: routes.find.path, Component: FindResultsPage },
      { path: routes.homeSales.path, Component: SalesHome },
      { path: routes.homeCsr.path, Component: CsrHome },
      // Side-by-side evaluation alternative — reached by URL only, see
      // CsrHomeAlt.tsx.
      { path: routes.homeCsrAlt.path, Component: CsrHomeAlt },
      { path: routes.homeOps.path, Component: OpsHome },
      { path: routes.homeFinance.path, Component: FinanceHome },
      { path: routes.tracking.path, Component: TrackingPage },
      { path: routes.stock.path, element: stockTier(<StockItemsPage />) },
      {
        path: routes.stockItemDetail.path,
        element: stockTier(<StockItemPage />),
      },
      {
        path: routes.stockReplenishment.path,
        element: replenishmentTier(<ReplenishmentProposalsPage />),
      },
      {
        path: routes.stockReplenishmentProposal.path,
        element: replenishmentTier(<ReplenishmentProposalPage />),
      },
      { path: routes.customers.path, Component: CustomersPage },
      { path: routes.customerCreate.path, Component: CustomerCreatePage },
      { path: routes.customerDetail.path, Component: CustomerDetailPage },
      { path: routes.customerOrders.path, Component: CustomerOrdersPage },
      {
        path: routes.customerOrderHistory.path,
        Component: CustomerOrderHistoryPage,
      },
      { path: routes.customerQuotes.path, Component: CustomerQuotesPage },
      { path: routes.customerSettings.path, Component: CustomerSettingsPage },
      { path: routes.customerInvoices.path, Component: CustomerInvoicesPage },
      { path: routes.customerPlanned.path, Component: CustomerPlannedPage },
      {
        path: routes.customerPortalAccess.path,
        Component: CustomerPortalAccessPage,
      },
      { path: routes.creditControl.path, Component: CreditControlPage },
      {
        path: routes.journeyTesting.path,
        Component: JourneyTestingConsolePage,
      },
      {
        path: routes.journeyTestingHome.path,
        Component: JourneyTestingConsolePage,
      },
      {
        path: routes.testingPreviewOpen.path,
        Component: TestingPreviewOpenPage,
      },
      { path: routes.tickets.path, Component: TicketsPage },
      { path: routes.ticketDetail.path, Component: TicketDetailPage },
      { path: routes.alerts.path, Component: AlertsPage },
      { path: routes.recommendations.path, Component: RecommendationsPage },
      { path: routes.notes.path, Component: NotesPage },
      { path: routes.communications.path, Component: CommunicationsPage },
      { path: routes.prospects.path, Component: ProspectsPage },
      { path: routes.leads.path, Component: LeadsPage },
      { path: routes.leadDetail.path, Component: LeadDetailPage },
      { path: routes.estimates.path, Component: EstimatesPage },
      {
        path: routes.accountAllocation.path,
        element: canManageAccountAllocationTier(<AccountAllocationPage />),
      },
      {
        path: routes.commercialExceptions.path,
        element: commercialTier(<CommercialExceptionsPage />),
      },
      {
        path: routes.carriageCosts.path,
        element: commercialTier(<CarriageCostsPage />),
      },
      {
        path: routes.createEstimate.path,
        Component: NewMerchandiseRequestPage,
      },
      { path: routes.catalogue.path, Component: CataloguePage },
      {
        path: routes.dictionaryWorkbook.path,
        element: techieTier(<DictionaryWorkbookPage />),
      },
      { path: routes.catalogueItem.path, Component: CatalogueItemPage },
      // Dictionary and Live costing both moved under
      // Settings › Catalogue (see the SettingsLayout children below) —
      // product-owner decision, 12 Sep,
      // docs/features/cost-engine-productionisation/03_manufacturing_surfaces_design.md
      // "IA — Manufacturing nests under Settings › Catalogue as Live
      // costing". These entries are pure redirect sources so bookmarks and
      // pasted links to the old `/catalogue/...` URLs keep resolving.
      {
        path: routes.catalogueDictionaryAdminLegacy.path,
        element: <Navigate to={paths.dictionaryAdmin()} replace />,
      },
      {
        path: routes.catalogueDictionaryRollingValidationLegacy.path,
        Component: LegacyDictionaryRollingValidationRedirect,
      },
      {
        path: routes.catalogueDictionaryCostLineLegacy.path,
        Component: LegacyDictionaryCostLineRedirect,
      },
      {
        path: routes.catalogueDictionaryItemAdminLegacy.path,
        Component: LegacyDictionaryItemAdminRedirect,
      },
      {
        path: routes.catalogueDictionaryCostPairLegacy.path,
        Component: LegacyDictionaryCostPairRedirect,
      },
      { path: routes.estimateDetail.path, Component: EstimateDetailPage },
      { path: routes.estimateTab.path, Component: EstimateDetailPage },
      {
        path: routes.estimateDetailLegacyTharstern.path,
        Component: LegacyTharsternEstimateRedirect,
      },
      { path: routes.contacts.path, Component: ContactsPage },
      { path: routes.contactDetail.path, Component: ContactDetailPage },
      { path: routes.jobs.path, Component: JobsPage },
      { path: routes.jobWorkspace.path, Component: JobWorkspacePage },
      { path: routes.orders.path, Component: OrdersPage },
      { path: routes.invoiceDetail.path, Component: SalesInvoicePage },
      { path: routes.proformaDetail.path, Component: ProformaPage },
      { path: routes.creditNoteDetail.path, Component: CreditNotePage },
      { path: routes.orderDetail.path, Component: OrderDetailPage },
      { path: routes.orderTab.path, Component: OrderDetailPage },
      { path: routes.quotes.path, Component: QuotesPage },
      { path: routes.quoteDetail.path, Component: QuotePage },
      {
        path: routes.purchaseOrderDetail.path,
        Component: PurchaseOrderDetailPage,
      },
      {
        path: routes.purchaseOrderTab.path,
        Component: PurchaseOrderDetailPage,
      },
      { path: routes.purchasing.path, Component: PurchasingPage },
      { path: routes.placement.path, Component: PlacementPage },
      { path: routes.promoOffice.path, Component: PromoOfficePage },
      // The old matching workbench's URLs, to One's purchase invoice list and record. `purchase-invoices/all` is
      // static and `:invoiceId` a parameter: the router ranks the static path first, whatever their order here.
      {
        path: routes.purchaseInvoices.path,
        Component: LegacyPurchaseInvoicesRedirect,
      },
      {
        path: routes.purchaseInvoiceIndex.path,
        Component: LegacyPurchaseInvoiceIndexRedirect,
      },
      {
        path: routes.purchaseInvoice.path,
        Component: LegacyPurchaseInvoiceRedirect,
      },
      {
        path: routes.varianceAuthorisation.path,
        Component: LegacyVarianceAuthorisationRedirect,
      },
      { path: routes.purchaseOrders.path, Component: PurchaseOrderQueuePage },
      { path: routes.suppliers.path, Component: SuppliersPage },
      { path: routes.supplier.path, Component: SupplierPage },
      { path: routes.supplierTab.path, Component: SupplierPage },
      {
        path: routes.financeAwaitingPayment.path,
        Component: AwaitingPaymentPage,
      },
      { path: routes.needsAttention.path, Component: NeedsAttentionPage },
      { path: routes.financeAgedDebt.path, Component: AgedDebtPage },
      { path: routes.financeAgedDebtTab.path, Component: AgedDebtPage },
      { path: routes.financeLedgerRules.path, Component: LedgerRulesPage },
      { path: routes.financeLedgerRulesTab.path, Component: LedgerRulesPage },
      { path: routes.prepressHome.path, Component: PrepressHomePage },
      { path: routes.issues.path, Component: IssuesPage },
      { path: routes.raiseIssue.path, Component: RaiseIssuePage },
      { path: routes.issueDetail.path, Component: IssueDetailPage },
      { path: routes.plan.path, Component: PlanPage },
      { path: routes.planTargets.path, Component: TargetPlanningPage },
      { path: routes.myPipeline.path, Component: MyPipelinePage },
      {
        path: routes.myPipelineArrivals.path,
        Component: ArrivalsQueuePage,
      },
      // Same page, a report's book. Registered after `arrivals` so the static
      // segment is the obvious winner to a reader; react-router ranks it above
      // the dynamic one either way.
      { path: routes.repPipeline.path, Component: MyPipelinePage },
      { path: routes.repPipelineArrivals.path, Component: ArrivalsQueuePage },
      { path: routes.interventionLog.path, Component: InterventionLogPage },
      { path: routes.forecastMovement.path, Component: MovementPage },
      {
        path: routes.planCycleReviewIndex.path,
        Component: PlanCycleReviewPage,
      },
      { path: routes.planCycleReview.path, Component: PlanCycleReviewPage },
      { path: routes.planRep.path, Component: PlanPage },
      { path: routes.planBucket.path, Component: PlanPage },
      // Backwards-compat redirect for the old /targets entry.
      {
        path: routes.targets.path,
        element: <Navigate to={paths.plan()} replace />,
      },
      {
        path: routes.featureSessions.path,
        element: techieTier(<FeatureSessionsPage />),
      },
      {
        path: routes.featureSessionWorkspace.path,
        element: techieTier(<FeatureSessionWorkspacePage />),
      },
      { path: routes.insights.path, Component: InsightsPage },
      {
        path: routes.insightsAccruals.path,
        Component: AccrualsUploadPage,
      },
      { path: routes.reports.path, Component: ReportsPage },
      { path: routes.repFigures.path, Component: RepCompanyFiguresPage },
      // Outbound Marketing & Sales Engine
      { path: routes.marketingCalendar.path, Component: CalendarPage },
      { path: routes.marketingCampaigns.path, Component: CampaignsPage },
      {
        path: routes.marketingCampaignWorkspace.path,
        Component: CampaignWorkspacePage,
      },
      { path: routes.marketingAudiences.path, Component: AudiencesPage },
      {
        path: routes.marketingAudienceBuilder.path,
        Component: AudienceBuilderPage,
      },
      { path: routes.marketingConsent.path, Component: MarketingConsentPage },
      { path: routes.marketingResults.path, Component: MarketingResultsPage },
      {
        path: routes.marketingLandingPages.path,
        Component: LandingPagesPage,
      },
      {
        path: routes.marketingLandingPageSubmissions.path,
        Component: LandingPageSubmissionsPage,
      },
      { path: routes.marketingTriggers.path, Component: MarketingTriggersPage },
      { path: routes.marketingTrigger.path, Component: MarketingTriggerPage },
      { path: routes.marketingPeople.path, Component: MarketingPeoplePage },
      {
        path: routes.marketingPersonDetail.path,
        Component: MarketingPersonDetailPage,
      },
      {
        Component: SettingsLayout,
        children: [
          {
            path: routes.settingsHome.path,
            element: <Navigate to={paths.settingsIntegrations()} replace />,
          },
          {
            path: routes.settingsIntegrations.path,
            Component: SettingsIntegrationsPage,
          },
          {
            path: routes.settingsSignals.path,
            element: adminOnly(<SettingsSignalsPage />),
          },
          {
            path: routes.settingsSignalsCreate.path,
            element: adminOnly(<SignalEditorPage />),
          },
          {
            path: routes.settingsSignalEdit.path,
            element: adminOnly(<SignalEditorPage />),
          },
          {
            path: routes.settingsAlerts.path,
            element: adminOnly(<SettingsAlertsPage />),
          },
          {
            path: routes.settingsPricingTargets.path,
            element: commercialTier(<SettingsPricingTargetsPage />),
          },
          {
            path: routes.settingsPricingReview.path,
            element: catalogueCuration(<SettingsPricingReviewPage />),
          },
          {
            path: routes.settingsAlertsCreate.path,
            element: adminOnly(<AlertEditorPage />),
          },
          {
            path: routes.settingsAlertEdit.path,
            element: adminOnly(<AlertEditorPage />),
          },
          {
            path: routes.settingsRecommendations.path,
            element: adminOnly(<SettingsRecommendationsPage />),
          },
          {
            path: routes.settingsRecommendationsCreate.path,
            element: adminOnly(<RecommendationEditorPage />),
          },
          {
            path: routes.settingsRecommendationEdit.path,
            element: adminOnly(<RecommendationEditorPage />),
          },
          {
            path: routes.settingsScenarios.path,
            element: adminOnly(<SettingsScenariosPage />),
          },
          {
            path: routes.settingsBulkDefinitions.path,
            element: adminOnly(<SettingsBulkDefinitionsPage />),
          },
          // Every staff role reads the rules (needs_attention:view); the
          // server presents Edit rule… to an admin only.
          {
            path: routes.settingsNeedsAttentionRules.path,
            element: <SettingsNeedsAttentionRulesPage />,
          },
          {
            path: routes.settingsCatalogues.path,
            element: catalogueCuration(<CataloguesPage />),
          },
          {
            path: routes.settingsCatalogueEdit.path,
            element: catalogueCuration(<CatalogueEditorPage />),
          },
          {
            path: routes.settingsPricing.path,
            element: catalogueCuration(<PriceGroupsPage />),
          },
          {
            path: routes.settingsPricingEdit.path,
            element: catalogueCuration(<SettingsPricingPage />),
          },
          {
            path: routes.settingsAssignments.path,
            element: catalogueCuration(<AssignmentsPage />),
          },
          // Dictionary — moved in from its own top-level `/catalogue/...`
          // URL (see the legacy redirects above) so the settings tab bar no
          // longer vanishes on it. The option library that sat beside it is
          // retired (decision 64).
          {
            path: routes.dictionaryAdmin.path,
            element: catalogueCuration(<DictionaryAdminPage />),
          },
          {
            path: routes.dictionaryGapLog.path,
            element: catalogueCuration(<DictionaryGapLogPage />),
          },
          {
            path: routes.dictionaryItemAdmin.path,
            element: catalogueCuration(<DictionaryItemAdminPage />),
          },
          {
            path: routes.dictionaryCostPair.path,
            element: catalogueCuration(<DictionaryCostPairPage />),
          },
          {
            // The item page's Mappings tab, addressed by path rather than
            // `?tab=` so the link from a cost drill-down stays stable.
            path: routes.dictionaryItemMappings.path,
            element: catalogueCuration(
              <DictionaryItemAdminPage routeTab="mappings" />,
            ),
          },
          // Live costing — Manufacturing renamed and folded in here
          // (product-owner decision, 12 Sep,
          // 03_manufacturing_surfaces_design.md). The bare path redirects
          // to Status; the five tabs share `SettingsLiveCostingLayout`'s
          // heading and tab strip. Checks stays `catalogueCuration`
          // (admin/manager, its tier before the move); the rest stay
          // `manufacturingSetupTier` (admin/manager/engineer).
          {
            path: routes.settingsLiveCosting.path,
            element: (
              <Navigate to={paths.settingsLiveCostingStatus()} replace />
            ),
          },
          {
            Component: SettingsLiveCostingLayout,
            children: [
              {
                path: routes.settingsLiveCostingStatus.path,
                element: manufacturingSetupTier(<LiveCostingStatusPage />),
              },
              {
                path: routes.settingsLiveCostingChecks.path,
                element: catalogueCuration(<LiveCostingChecksPage />),
              },
              {
                path: routes.settingsLiveCostingCostLine.path,
                element: catalogueCuration(<LiveCostingCostLinePage />),
              },
              {
                path: routes.settingsLiveCostingRoutingPolicy.path,
                element: manufacturingSetupTier(
                  <LiveCostingRoutingPolicyPage />,
                ),
              },
              {
                path: routes.settingsLiveCostingManufacturingSetup.path,
                element: manufacturingSetupTier(<ManufacturingSetupPage />),
              },
              {
                path: routes.settingsLiveCostingCostCentreDetail.path,
                element: manufacturingSetupTier(
                  <ManufacturingSetupCostCentreDetailPage />,
                ),
              },
              {
                path: routes.settingsLiveCostingInkProfileDetail.path,
                element: manufacturingSetupTier(
                  <ManufacturingSetupInkProfileDetailPage />,
                ),
              },
              {
                path: routes.settingsLiveCostingProductTypeDetail.path,
                element: manufacturingSetupTier(
                  <ManufacturingSetupProductTypeDetailPage />,
                ),
              },
              {
                path: routes.settingsLiveCostingMaterialDetail.path,
                element: manufacturingSetupTier(
                  <ManufacturingSetupMaterialDetailPage />,
                ),
              },
              {
                path: routes.settingsLiveCostingCheckSettings.path,
                element: manufacturingSetupTier(
                  <LiveCostingCheckSettingsPage />,
                ),
              },
            ],
          },
          // Legacy `/settings/manufacturing/...` redirect sources — see the
          // `/catalogue/...` legacy redirects above for the same pattern.
          {
            path: routes.settingsManufacturingSetupLegacy.path,
            element: (
              <Navigate
                to={paths.settingsLiveCostingManufacturingSetup()}
                replace
              />
            ),
          },
          {
            path: routes.settingsManufacturingCostCentreDetailLegacy.path,
            Component: LegacyManufacturingCostCentreDetailRedirect,
          },
          {
            path: routes.settingsManufacturingInkProfileDetailLegacy.path,
            Component: LegacyManufacturingInkProfileDetailRedirect,
          },
          {
            path: routes.settingsManufacturingProductTypeDetailLegacy.path,
            Component: LegacyManufacturingProductTypeDetailRedirect,
          },
          {
            path: routes.settingsManufacturingMaterialDetailLegacy.path,
            Component: LegacyManufacturingMaterialDetailRedirect,
          },
          {
            path: routes.settingsManufacturingRoutingPolicyLegacy.path,
            element: (
              <Navigate to={paths.settingsLiveCostingRoutingPolicy()} replace />
            ),
          },
          {
            path: routes.settingsManufacturingLiveCostingLegacy.path,
            element: (
              <Navigate to={paths.settingsLiveCostingStatus()} replace />
            ),
          },
          {
            path: routes.settingsJobs.path,
            element: adminOnly(<SettingsJobsPage />),
          },
          {
            path: routes.settingsUsers.path,
            element: adminOnly(<AdminUsersPage />),
          },
          {
            path: routes.settingsPatternDiscovery.path,
            element: adminOnly(<PatternDiscoveryPage />),
          },
          {
            path: routes.settingsWallboard.path,
            element: adminOnly(<SettingsWallboardPage />),
          },
          {
            path: routes.settingsShopfloorKiosk.path,
            element: adminOnly(<SettingsShopfloorKioskPage />),
          },
          {
            path: routes.settingsFeatures.path,
            element: canReadFeaturesTier(<SettingsFeaturesPage />),
          },
          {
            path: routes.settingsFeedback.path,
            element: adminOnly(<SettingsFeedbackPage />),
          },
          {
            path: routes.settingsFeedbackDetail.path,
            element: adminOnly(<SettingsFeedbackDetailPage />),
          },
          {
            path: routes.settingsTicketCategories.path,
            element: canManageTicketCategoriesTier(
              <SettingsTicketCategoriesPage />,
            ),
          },
          {
            path: routes.settingsBuddies.path,
            element: canManageBuddyListsTier(<SettingsBuddiesPage />),
          },
          {
            path: routes.settingsDepartments.path,
            element: canManageDepartmentsTier(<SettingsDepartmentsPage />),
          },
          {
            path: routes.settingsCatalogueMigration.path,
            element: adminOnly(<SettingsCatalogueMigrationPage />),
          },
          {
            path: routes.settingsCatalogueMigrationCustomer.path,
            element: adminOnly(<SettingsCatalogueMigrationCustomerPage />),
          },
          {
            path: routes.settingsEmailCaptureExclusions.path,
            element: canManageEmailCaptureExclusionsTier(
              <SettingsEmailCaptureExclusionsPage />,
            ),
          },
        ],
      },
      { path: "*", Component: NotFoundPage },
    ]),
  },
  // Public, capability-URL gated routes — deliberately NOT feature-gated here.
  // The resolved feature map arrives on `/me`, which requires authentication,
  // and these routes exist precisely to be reachable without it. They are
  // gated server-side instead, at environment level on their API endpoints
  // (see `require_environment_feature` in apps/api/app/features_routes.py):
  // the page loads and fails to fetch, which for a capability URL pointed at
  // an environment where the feature is off is the correct outcome.
  {
    path: routes.sharedPreview.path,
    Component: SharedPreviewPage,
  },
  {
    path: routes.wallboard.path,
    Component: WallboardPage,
  },
  {
    path: routes.exeterBoard.path,
    Component: ExeterBoardPage,
  },
  {
    path: routes.shopfloorKiosk.path,
    Component: ShopfloorKioskPage,
  },

  // Customer portal routes moved to `apps/customer-portal` — a separate web
  // shell on its own hostname, not part of this app's route table anymore.
  // See docs/features/customer-storefront/. `routes.portalOrders` etc. and
  // `paths.portalOrders()` etc. remain in shared-core/routing/routes.ts as
  // the canonical manifest the portal app itself consumes.

  // Authenticated but chrome-less (no dashboard nav) for a clean print
  // layout — NOT in PUBLIC_ROUTE_PATHS, so normal sign-in still applies.
  // Manually feature-gated since it sits outside gateShellRoutes' scope.
  {
    path: routes.promoOfficeQuotePrint.path,
    element: (
      <FeatureGate feature={routes.promoOfficeQuotePrint.feature}>
        <PromoOfficeQuotePrintPage />
      </FeatureGate>
    ),
  },
];

// One pathless layout route wraps the whole tree so FaroRouteTracker can
// observe every route change (SPA navigations included) from inside the
// router context. `routeConfig` itself stays unwrapped for tests.
export const router = createBrowserRouter([
  { element: <FaroRouteTracker />, children: routeConfig },
]);
